import { Translator, useTranslations } from 'i18n';
import { NotificationView } from 'notifications/notification-view';
import React from 'react';
import { NotificationTypeProps } from './notification-type-props';
import { parseJSON } from 'date-fns';
import { Button } from '@SBGSports/referee-react';
import { DismissButton } from 'notifications/buttons';
import { TfNotification } from 'api';
import { useHistory } from 'react-router-dom';
import { useProcessingStatusStore } from 'shared/state/processingStatusStore';

type DatasharingNotificationPayload = {
    account_name?: string;
    account_link_id?: string;
    share_agreement_id?: string;
};

export function getNotificationTitle(type: string, __: Translator) {
    if (type === 'ds_code_accepted') {
        return __('datasharing.notification_code_accepted');
    }

    if (type === 'ds_new_share') {
        return __('datasharing.notification_new_share');
    }

    if (type === 'ds_share_accepted') {
        return __('datasharing.notification_share_accepted');
    }

    if (type === 'ds_share_declined') {
        return __('datasharing.notification_share_declined');
    }

    if (type === 'ds_share_cancelled') {
        return __('datasharing.notification_share_cancelled');
    }

    if (type === 'ds_import_complete') {
        return __('datasharing.notification_import_complete');
    }

    return undefined;
}

export function getNotificationContents(type: string, payload: DatasharingNotificationPayload, __: Translator) {
    if (type === 'ds_code_accepted') {
        return (
            <>
                <b>
                    {__('datasharing.notification_code_accepted_content_1', {
                        account_name: payload.account_name ?? '',
                    })}
                </b>
                <br />
                {__('datasharing.notification_code_accepted_content_2')}
            </>
        );
    }

    if (type === 'ds_new_share') {
        return (
            <>{__('datasharing.notification_new_share_agreement_sub', { account_name: payload.account_name ?? '' })}</>
        );
    }

    if (type === 'ds_share_accepted') {
        return <>{__('datasharing.notification_share_accepted_sub', { account_name: payload.account_name ?? '' })}</>;
    }

    if (type === 'ds_share_declined') {
        return <>{__('datasharing.notification_share_declined_sub', { account_name: payload.account_name ?? '' })}</>;
    }

    if (type === 'ds_share_cancelled') {
        return <>{__('datasharing.notification_share_cancelled_sub', { account_name: payload.account_name ?? '' })}</>;
    }

    if (type === 'ds_import_complete') {
        return (
            <>
                <b>
                    {__('datasharing.notification_import_complete_sub', { account_name: payload.account_name ?? '' })}
                </b>
                <br />
                {__('datasharing.notification_import_complete_sub_2')}
            </>
        );
    }

    return undefined;
}

export function getNotificationActions(
    notification: TfNotification,
    history: ReturnType<typeof useHistory>,
    setOpenModal: (openModal: boolean) => void,
    __: Translator,
) {
    const payload = notification.payload as DatasharingNotificationPayload;
    const type = notification.type as string;
    const actions = [<DismissButton key="dismiss" notification={notification} data-testid={'dismiss-' + type} />];

    const viewShareAgreement = {
        label: __('settings_migration.view_details'),
        onClick: () => history.push('/settings/share-centre?view_share=' + payload.share_agreement_id),
    };

    const config: Record<string, { label: string; onClick: () => void } | undefined> = {
        ds_code_accepted: {
            label: __('datasharing.view_connection'),
            onClick: () => history.push('/settings/share-centre/account-connections/' + payload.account_link_id),
        },
        ds_new_share: {
            label: __('datasharing.review_action'),
            onClick: () => history.push('/settings/share-centre?goto_review=' + payload.share_agreement_id),
        },
        ds_share_accepted: viewShareAgreement,
        ds_share_declined: viewShareAgreement,
        ds_share_cancelled: viewShareAgreement,
        ds_import_complete: {
            label: __('datasharing.view_processing'),
            onClick: () => setOpenModal(true),
        },
    };

    const action = config[type];

    if (action) {
        actions.push(
            <Button size="small" key="action" onClick={action.onClick} data-testid={action.label + '-' + type}>
                {action.label}
            </Button>,
        );
    }

    return actions;
}

export const DataSharing: React.FC<NotificationTypeProps> = (props) => {
    const { notification, handleToggleRead } = props;

    const { __ } = useTranslations();
    const createdAt = parseJSON(notification.created_at);
    const history = useHistory();
    const { setOpenModal } = useProcessingStatusStore();

    if (!notification.payload) {
        return <></>;
    }

    const title = getNotificationTitle(notification.type as string, __);
    const actions = getNotificationActions(notification, history, setOpenModal, __);
    const contents = getNotificationContents(
        notification.type as string,
        notification.payload as DatasharingNotificationPayload,
        __,
    );

    if (!title || !contents) {
        return <></>;
    }

    return (
        <NotificationView
            title={title}
            contents={contents}
            isRead={notification.read === 1}
            onToggleRead={handleToggleRead}
            createdAt={createdAt}
            actions={actions}
        />
    );
};
